import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface ISubmitRejectedResidentPermitRequest {
  residentPermit: string;
}

interface ISubmitRejectedResidentPermitResponse {
  success: boolean;
  message: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISubmitRejectedResidentPermitResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const submitRejectedResidentPermitAsync = createAsyncThunk(
  'residentPermit/submitRejected',
  async (
    values: ISubmitRejectedResidentPermitRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const firstName = state.auth.value?.data?.user?.firstName;
      const lastName = state.auth.value?.data?.user?.lastName;
      const email = state.auth.value?.data?.user?.email;
      const phoneNumber = state.auth.value?.data?.user?.phoneNumber;
      const gender = state.auth.value?.data?.user?.gender;
      const dateOfBirth = state.auth.value?.data?.user?.dateOfBirth;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      };

      const { data } = await api.put(
        '/representative-nationality-verification',
        {
          ...values,
          firstName,
          lastName,
          email,
          phoneNumber,
          gender,
          dateOfBirth,
        },
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ISubmitRejectedResidentPermitResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const submitRejectedResidentPermitSlice = createSlice({
  name: 'residentPermit/submitRejected',
  initialState,
  reducers: {
    resetSubmitRejectedResidentPermit: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitRejectedResidentPermitAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(submitRejectedResidentPermitAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISubmitRejectedResidentPermitResponse;
      })
      .addCase(submitRejectedResidentPermitAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ISubmitRejectedResidentPermitResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetSubmitRejectedResidentPermit } =
  submitRejectedResidentPermitSlice.actions;

export default submitRejectedResidentPermitSlice.reducer;
